import type React from "react";
import {Skeleton, Typography} from "@mui/material";
import {ChecklistOutlined} from "@mui/icons-material";
import {StatsCard, type StatsCardItem} from "~/components/Card/StatsCard";
import {EMPTY_CELL} from "~/components/data-grid/cells/gridCellFormat";
import VerifiedIcon from '~/icons/svg/VerifiedIcon.svg?react'
import InfoIcon from '~/icons/svg/InfoIcon.svg?react'
import WarningCircleIcon from '~/icons/svg/WarningCircleIcon.svg?react'
import ScheduleIcon from '~/icons/svg/ScheduleIcon.svg?react'
import type {ComplianceKpiSummary} from "~/pages/supplier-360/types/Supplier360Types";
import {useComplianceKpiSummaryQuery} from "~/pages/supplier-360/tabs/SupplierCompliance/hooks/useComplianceKpiSummaryQuery";

/**
 * The 5 KPI cells (§3.7) — real per-supplier totals from `kpi_summary` (§2.8), so they cover
 * every record, not just the table's current page. `unmet` is `missing` + `expired` combined.
 */
function buildKpiItems(summary: ComplianceKpiSummary | undefined): StatsCardItem[] {
    const value = (n: number | undefined) => n === undefined ? EMPTY_CELL : String(n);

    return [
        {
            icon: <ChecklistOutlined/>,
            iconColor: 'primary',
            label: 'Records',
            value: value(summary?.records),
            tagText: summary
                ? `${summary.company_wide} company-wide · ${summary.business_unit} business-unit`
                : EMPTY_CELL,
        },
        {
            icon: <VerifiedIcon/>,
            iconColor: 'success',
            label: 'Met',
            value: value(summary?.met),
            tagText: 'evidence current',
        },
        {
            icon: <ScheduleIcon/>,
            iconColor: 'warning',
            label: 'Expiring soon',
            value: value(summary?.expiring_soon),
            tagText: 'within 60 days',
        },
        {
            icon: <WarningCircleIcon/>,
            iconColor: 'error',
            label: 'Unmet',
            value: value(summary?.unmet),
            tagText: 'missing or lapsed',
        },
        {
            icon: <InfoIcon/>,
            iconColor: 'info',
            label: 'Pending review',
            value: value(summary?.pending_review),
            tagText: 'awaiting verification',
        },
    ];
}

type ComplianceKpisProps = {
    entityId: number;
};

/** The Compliance tab's KPI strip — fetches its own `kpi_summary`. */
export const ComplianceKpis: React.FC<ComplianceKpisProps> = ({entityId}) => {
    const {data: summary, isLoading, isError} = useComplianceKpiSummaryQuery(entityId);

    if (isLoading) {
        // The skeleton takes its size from the empty strip inside it, so nothing jumps when data lands.
        return (
            <Skeleton variant="rectangular" sx={{maxWidth: 'none', borderRadius: 1}}>
                <StatsCard items={buildKpiItems(undefined)}/>
            </Skeleton>
        );
    }

    if (isError) {
        return (
            <Typography color="text.secondary">
                Could not load compliance KPIs.
            </Typography>
        );
    }

    return <StatsCard items={buildKpiItems(summary)}/>;
};
